import { addLocalDays, businessDate } from "./schedulingOperations";
export const bookingResourceId = booking => booking.schedulingResourceId ?? booking.resourceId ?? "unassigned";

export function calendarDates(from, to) {
  const days = [];
  for (let date = from; date && date <= to && days.length < 62; date = addLocalDays(date, 1)) days.push(date);
  return days;
}

const localMinutes = (instant, timeZone) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone, hourCycle: "h23", hour: "2-digit", minute: "2-digit" }).formatToParts(instant).map(part => [part.type, part.value]));
  return Number(parts.hour) * 60 + Number(parts.minute);
};

// Split overnight appointments across business-local dates. End times are
// exclusive, so an appointment ending at midnight never spills into the next day.
export function calendarSegments(items, from, to, timeZone) {
  const days = calendarDates(from, to);
  const result = Object.fromEntries(days.map(date => [date, []]));
  items.forEach(item => {
    const booking = item.booking;
    const start = new Date(booking.serviceStartsAtUtc);
    const end = new Date(booking.serviceEndsAtUtc || start.getTime() + booking.durationMinutes * 60000);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) return;
    const startDate = businessDate(timeZone, start), endDate = businessDate(timeZone, end);
    days.filter(date => date >= startDate && date <= endDate).forEach(date => {
      const startMinute = date === startDate ? localMinutes(start, timeZone) : 0;
      const endMinute = date === endDate ? localMinutes(end, timeZone) : 1440;
      if (date === endDate && endMinute === 0) return;
      // Repeated clock hours during DST can have a smaller wall-clock end.
      result[date].push({ item, date, startMinute, endMinute: endMinute > startMinute ? endMinute : Math.min(1440, startMinute + (end - start) / 60000) });
    });
  });
  return result;
}

// Expand occupied time intervals enough to fit their cards. Every resource uses
// the same positions so adjacent slots align with the time labels and each other.
export function calendarTimeGrid(events, fromMinute, toMinute, minimumEventHeight = 88, pixelsPerHour = 72) {
  const minutes = [...new Set([
    fromMinute, toMinute,
    ...Array.from({ length: Math.ceil((toMinute - fromMinute) / 60) }, (_, index) => fromMinute + index * 60),
    ...events.flatMap(event => [event.startMinute, event.endMinute]),
  ])].sort((a, b) => a - b);
  const endings = new Map();
  events.forEach(event => endings.set(event.endMinute, [...(endings.get(event.endMinute) || []), event]));
  const positions = new Map([[minutes[0], 0]]);
  minutes.slice(1).forEach((minute, index) => {
    const previous = minutes[index];
    const baseline = positions.get(previous) + (minute - previous) / 60 * pixelsPerHour;
    positions.set(minute, Math.max(baseline, ...(endings.get(minute) || []).map(event => positions.get(event.startMinute) + minimumEventHeight + 4)));
  });
  const hours = minutes.filter(minute => minute % 60 === 0);
  const ticks = [];
  minutes.filter(minute => minute < toMinute).forEach(minute => {
    const top = positions.get(minute);
    if (minute % 60 === 0 || (hours.every(hour => Math.abs(top - positions.get(hour)) >= 24) && (!ticks.length || top - positions.get(ticks[ticks.length - 1]) >= 24))) ticks.push(minute);
  });
  return { positions, ticks, height: positions.get(toMinute) };
}

export function layoutCalendarEvents(events) {
  const result = [];
  let cluster = [], laneEnds = [], clusterEnd = -1;
  const finish = () => {
    result.push(...cluster.map(event => ({ ...event, laneCount: laneEnds.length })));
    cluster = []; laneEnds = []; clusterEnd = -1;
  };
  [...events].sort((a, b) => a.startMinute - b.startMinute || b.endMinute - a.endMinute).forEach(event => {
    if (event.startMinute >= clusterEnd && cluster.length) finish();
    let lane = laneEnds.findIndex(end => end <= event.startMinute);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = event.endMinute;
    cluster.push({ ...event, lane }); clusterEnd = Math.max(clusterEnd, event.endMinute);
  });
  finish();
  return result;
}
